import { Dispatch, Reducer, useCallback, useRef } from 'react'

export type Listener<S, A> = (update: { prevState: S; nextState: S; action: A }) => void
export type Subscribe<S, A> = (listener: Listener<S, A>) => () => void

export function useQueuedDispatch<S, A>({
  state,
  dispatch,
  reducer,
  onStateChange,
}: {
  state: S
  dispatch: Dispatch<A>
  reducer: Reducer<S, A>
  onStateChange?: Listener<S, A>
}): [Dispatch<A>, Subscribe<S, A>] {
  // Only store what truly needs to persist across renders
  const refs = useRef({
    listeners: new Set<Listener<S, A>>(),
    queue: [] as A[],
    isProcessing: false,
    nextState: state,
  })

  // reset the nextState to the current state
  refs.current.nextState = state

  const processDispatchQueue = useCallback(() => {
    const { queue, listeners } = refs.current

    if (refs.current.isProcessing || queue.length === 0) {
      return
    }

    refs.current.isProcessing = true

    // remove all items from the queue, and reduce them to the next state
    refs.current.nextState = queue.splice(0).reduce((prevState, action) => {
      const nextState = reducer(prevState, action)
      const update = { prevState, action, nextState }
      // Notify external handler (e.g., Redux DevTools)
      onStateChange?.(update)
      //notify subscribers
      listeners.forEach(listener => listener(update))
      //dispatch to React's reducer
      dispatch(action)
      //return the next state
      return nextState
    }, refs.current.nextState)

    refs.current.isProcessing = false

    // Check if more actions were queued while processing
    if (queue.length > 0) {
      processDispatchQueue()
    }
  }, [dispatch, onStateChange, reducer])

  const queuedDispatch: Dispatch<A> = useCallback(
    (action: A) => {
      refs.current.queue.push(action)

      // Schedule processing if not already scheduled
      if (!refs.current.isProcessing) {
        processDispatchQueue()
      }
    },
    [processDispatchQueue]
  )

  const subscribe: Subscribe<S, A> = useCallback((listener: Listener<S, A>) => {
    refs.current.listeners.add(listener)

    // Return unsubscribe function
    return () => {
      refs.current.listeners.delete(listener)
    }
  }, [])

  return [queuedDispatch, subscribe]
}
